import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { legacyApiService, LegacyEvent } from '../services/api-compat';
import './EventList.css';

// Component for handling event images with proper error handling
const EventImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [imgSrc, setImgSrc] = useState<string>(src || '/placeholder-event.svg');
  const [hasErrored, setHasErrored] = useState(false);

  const handleError = () => {
    if (!hasErrored && !imgSrc.endsWith('/placeholder-event.svg')) {
      setHasErrored(true);
      setImgSrc('/placeholder-event.svg');
    }
  };

  return (
    <img 
      src={imgSrc}
      alt={alt}
      onError={handleError}
    />
  );
};

const EventList: React.FC = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<LegacyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsData = await legacyApiService.getEvents();
        setEvents(eventsData);
      } catch (err) {
        setError(t('events.loadError'));
        console.error('Error fetching events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [t]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">{t('events.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <h2 className="page-title">{t('events.upcomingEvents')}</h2>
      <div className="events-grid">
        {events.map((event) => (
          <div key={event.id} className="event-card">
            <div className="event-image">
              <EventImage 
                src={event.imageUrl || '/placeholder-event.svg'} 
                alt={event.title}
              />
            </div>
            <div className="event-content">
              <h3 className="event-title">{event.title}</h3>
              <p className="event-description">
                {event.description.length > 150 
                  ? `${event.description.substring(0, 150)}...` 
                  : event.description}
              </p>
              <div className="event-meta">
                <div className="event-location">
                  <span>📍</span>
                  <span>{event.location}</span>
                </div>
                <div className="event-date">
                  <span>📅</span>
                  <span>{new Date(event.dateTime).toLocaleDateString()} {t('events.at')} {new Date(event.dateTime).toLocaleTimeString()}</span>
                </div>
                <div className="event-price">
                  <span>💰</span>
                  <span>€{event.price.toFixed(2)}</span>
                </div>
                <div className="event-capacity">
                  <span>👥</span>
                  <span>{event.availableTickets || event.maxAttendees} {t('events.ticketsAvailable')}</span>
                </div>
              </div>
              <Link to={`/events/${event.id}`} className="btn btn-primary">
                {t('events.viewDetails')}
              </Link>
            </div>
          </div>
        ))}
      </div>
      {events.length === 0 && (
        <div className="no-events">
          <p>{t('events.noEvents')}</p>
        </div>
      )}
    </div>
  );
};

export default EventList;
